import "react-toastify/dist/ReactToastify.css";

import { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";

import { AbstractConnector } from "@web3-react/abstract-connector";
import { UnsupportedChainIdError, useWeb3React } from "@web3-react/core";
import { WalletConnectConnector } from "@web3-react/walletconnect-connector";
import { WalletLinkConnector } from "@web3-react/walletlink-connector";

import Header from "../components/Header";
import WalletModal from "../components/WalletModal";
import { useGnarlyGoatsContractsState } from "../hooks/useGnarlyGoatsContracts";
import { useEagerConnect, useInactiveListener } from "../hooks/web3";
import AboutPage from "./AboutPage";
import HomePage from "./HomePage";
import MemberPage from "./MemberPage";
import OwnerPage from "./OwnerPage";
import ProvenancePage from "./ProvenancePage";

const AppWrapper = styled.div``

const HeaderWrapper = styled.div`
	${({ theme }) => theme.flexRowNoWrap}
	width: 100%;
	justify-content: space-between;
`

const BodyWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	max-width: 100%;
`

const ViewportAnchor = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 100vh;
	position: relative;
`

const App = (props: any) => {
	const { active, account, connector, activate, error } = useWeb3React()

	const { isOwner } = useGnarlyGoatsContractsState()

	const [activatingConnector, setActivatingConnector] = useState<any>()

	// handle logic to eagerly connect to the injected ethereum provider, if it exists and has granted access already
	const triedEager = useEagerConnect()

	// handle logic to connect in reaction to certain events on the injected ethereum provider, if it exists
	useInactiveListener(!triedEager || !!activatingConnector)

	useEffect(() => {
		if (activatingConnector && activatingConnector === connector) {
			setActivatingConnector(undefined)
		}
	}, [activatingConnector, connector])

	const tryActivation = async (connector: AbstractConnector | undefined) => {
		setActivatingConnector(connector)

		// if the connector is walletconnect and the user has already tried to connect, manually reset the connector
		if (connector instanceof WalletConnectConnector) {
			connector.walletConnectProvider = undefined
		}

		if (connector instanceof WalletLinkConnector) {
			connector.walletLink = undefined
		}

		if (connector !== undefined) {
			try {
				await activate(connector, undefined, true)
				setActivatingConnector(undefined)
				const acc = (await connector.getAccount()) as string
				toast.success(
					`Connected to ${acc.slice(0, 5)}...${acc.slice(
						acc.length - 4,
						acc.length
					)}`
				)
			} catch (err) {
				if (error instanceof UnsupportedChainIdError) {
					activate(connector) // a little janky...can't use setError because the connector isn't set
				} else {
					setActivatingConnector(undefined)
				}
				toast.error('Error connecting to wallet.')
				console.log(err)
			}
		}
	}

	return (
		<ViewportAnchor>
			<AppWrapper>
				<WalletModal tryActivation={tryActivation} toggle />

				<HeaderWrapper>
					<Header></Header>
				</HeaderWrapper>

				{
					<BodyWrapper>
						<Routes>
							{<Route path="/" element={<HomePage />} />}
							<Route path="/provenance" element={<ProvenancePage />} />
							<Route path="/about" element={<AboutPage />} />
							{!!active && <Route path="/members" element={<MemberPage />} />}
							{!!active && !!account && !!isOwner && (
								<Route path="/owner" element={<OwnerPage />} />
							)}
						</Routes>
					</BodyWrapper>
				}
			</AppWrapper>
		</ViewportAnchor>
	)
}

export default App
